/**
 * Inverse of the wsapi `string.ToHtmlEntities()` encoder. Descriptions are stored
 * server-side as HTML entities (e.g. "Acme&trade;") so they drop cleanly into
 * legacy/PDF/email rendering contexts. React renders text verbatim, so we decode
 * back to display characters at the GraphQL response boundary.
 */

const namedEntities: Record<string, string> = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: '"',
	apos: "'",
	nbsp: " ",
	iexcl: "¡",
	cent: "¢",
	pound: "£",
	curren: "¤",
	yen: "¥",
	brvbar: "¦",
	sect: "§",
	uml: "¨",
	copy: "©",
	ordf: "ª",
	laquo: "«",
	not: "¬",
	shy: "­",
	reg: "®",
	macr: "¯",
	deg: "°",
	plusmn: "±",
	sup2: "²",
	sup3: "³",
	acute: "´",
	micro: "µ",
	para: "¶",
	middot: "·",
	cedil: "¸",
	sup1: "¹",
	ordm: "º",
	raquo: "»",
	frac14: "¼",
	frac12: "½",
	frac34: "¾",
	iquest: "¿",
	Agrave: "À",
	Aacute: "Á",
	Acirc: "Â",
	Atilde: "Ã",
	Auml: "Ä",
	Aring: "Å",
	AElig: "Æ",
	Ccedil: "Ç",
	Egrave: "È",
	Eacute: "É",
	Ecirc: "Ê",
	Euml: "Ë",
	Igrave: "Ì",
	Iacute: "Í",
	Icirc: "Î",
	Iuml: "Ï",
	ETH: "Ð",
	Ntilde: "Ñ",
	Ograve: "Ò",
	Oacute: "Ó",
	Ocirc: "Ô",
	Otilde: "Õ",
	Ouml: "Ö",
	times: "×",
	Oslash: "Ø",
	Ugrave: "Ù",
	Uacute: "Ú",
	Ucirc: "Û",
	Uuml: "Ü",
	Yacute: "Ý",
	THORN: "Þ",
	szlig: "ß",
	agrave: "à",
	aacute: "á",
	acirc: "â",
	atilde: "ã",
	auml: "ä",
	aring: "å",
	aelig: "æ",
	ccedil: "ç",
	egrave: "è",
	eacute: "é",
	ecirc: "ê",
	euml: "ë",
	igrave: "ì",
	iacute: "í",
	icirc: "î",
	iuml: "ï",
	eth: "ð",
	ntilde: "ñ",
	ograve: "ò",
	oacute: "ó",
	ocirc: "ô",
	otilde: "õ",
	ouml: "ö",
	divide: "÷",
	oslash: "ø",
	ugrave: "ù",
	uacute: "ú",
	ucirc: "û",
	uuml: "ü",
	yacute: "ý",
	thorn: "þ",
	yuml: "ÿ",
	ndash: "–",
	mdash: "—",
	lsquo: "‘",
	rsquo: "’",
	ldquo: "“",
	rdquo: "”",
	hellip: "…",
	euro: "€",
	trade: "™"
};

const ENTITY_PATTERN = /&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g;
const DESCRIPTION_KEY = "description";

/**
 * Decodes a single string. Returns the input unchanged if it has no entities.
 */
export const decodeHtmlEntities = (input: string): string => {
	if (!input || input.indexOf("&") === -1) return input;
	return input.replace(ENTITY_PATTERN, (match, body: string) => {
		if (body[0] === "#") {
			const code =
				body[1] === "x" || body[1] === "X" ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
			return Number.isFinite(code) ? String.fromCodePoint(code) : match;
		}
		return namedEntities[body] ?? match;
	});
};

/**
 * Recursively walks a GraphQL response value and decodes any field named
 * `description` (case-insensitive) in place. Fast path: returns immediately
 * for non-objects. Mutates the input — safe because RTK Query stores the
 * decoded value in cache and never re-uses the encoded source.
 */
export const decodeDescriptionsInResponse = (value: unknown): void => {
	if (value === null || value === undefined || typeof value !== "object") return;
	if (Array.isArray(value)) {
		for (const item of value) decodeDescriptionsInResponse(item);
		return;
	}
	const record = value as Record<string, unknown>;
	for (const key of Object.keys(record)) {
		const child = record[key];
		if (key.toLowerCase() === DESCRIPTION_KEY && typeof child === "string") {
			record[key] = decodeHtmlEntities(child);
		} else if (child !== null && typeof child === "object") {
			decodeDescriptionsInResponse(child);
		}
	}
};
